import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import useSharedLink from "../SharedLink/index";
import axios from "axios";
import { xorDecrypt } from "../encrypt";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axiosInstance from "../api/axiosInstance";

const GetClient = forwardRef((props, ref) => {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false); // State for confirmation dialog
  const [snackbarOpen, setSnackbarOpen] = useState(false); // State for snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // State for snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // State for snackbar severity
  const sharedLink = useSharedLink();

  useImperativeHandle(ref, () => ({
    refreshClientList: () => {
      fetchData(sharedLink, setClients);
    }
  }));

  const fetchData = async (sharedLink, setClients) => {
    setLoading(true);
    try {
      const encryptedSubscriberId = localStorage.getItem("subscriberId");
      //console.log("Encrypted Subscriber ID:", encryptedSubscriberId);

      const subscriberId = xorDecrypt(encryptedSubscriberId);
      //console.log("Decrypted Subscriber ID:", subscriberId);

      if (subscriberId === 0) {
        console.warn("Subscriber ID is 0, check decryption logic.");
      }

      const response = await axiosInstance.post(
        `${sharedLink}/api/abanotes/getclient`,
        { subscriberid: subscriberId }
      );

      const data = await response.data;
      //console.log("Fetched client data:", data);

      if (Array.isArray(data) && data.length > 0) {
        const clientsWithId = data.map((client, index) => ({
          ...client,
          id: client.id || index, // Ensure each client has a unique id
        }));
        setClients(clientsWithId);
      } else if (data.message) {
        console.warn("Error message received:", data.message);
        setSnackbarMessage(data.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setClients([]); // Clear clients if there's an error message
      } else {
        console.warn("No valid client data found.");
        setClients([]); // Clear clients if no valid data is found
      }
    } catch (error) {
      console.error("There was an error fetching the client data!", error);
      setSnackbarMessage("There was an error fetching the client data!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setClients([]); // Clear clients if there's an error
    } finally {
      setLoading(false);
    }
  };

  const refreshClientList = () => {
    fetchData(sharedLink, setClients);
  };

  useEffect(() => {
    fetchData(sharedLink, setClients)
      .then((message) => console.log(message))
      .catch((error) => console.error(error));
  }, [sharedLink]);

  const handleEditClick = (client) => {
    const formattedClient = {
      ...client,
      dateOfBirth: client.dateOfBirth
        ? new Date(client.dateOfBirth).toISOString().split("T")[0]
        : "",
    };
    setEditClient(formattedClient);
    setOpen(true);
  };

  const handleClose = () => {
    setEditClient(null);
    setOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const handleSave = () => {
    const today = new Date().toISOString().split("T")[0];
    if (editClient?.dateOfBirth > today) {
      setSnackbarMessage("Date of birth cannot be in the future.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setConfirmOpen(true); // Open confirmation dialog
  };

  const handleConfirmSave = () => {
    if (!editClient) {
      console.error("No client selected for update");
      return;
    }

    const subscriberId = xorDecrypt(localStorage.getItem("subscriberId"));
    if (!subscriberId) {
      console.error("Subscriber ID not found in local storage");
      return;
    }

    const updatedClient = {
      ...editClient,
      subscriber: {
        id: subscriberId,
      },
    };

    //console.log("Updated client data being sent:", updatedClient);

    axiosInstance
      .post(`${sharedLink}/api/abanotes/updateclient`, updatedClient, {
        withCredentials: true,
      })
      .then((response) => {
        //console.log("Client updated successfully", response.data);
        fetchData(sharedLink, setClients);
        handleClose();
        setSnackbarMessage("Client updated successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error("Failed to update client", error);
        setSnackbarMessage("Failed to update client");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        handleConfirmClose(); // Close confirmation dialog
      });
  };

  const columns = [
    { field: "clientName", headerName: "Client Name", flex: 1 },
    {
      field: "profileDetails",
      headerName: "Profile Details",
      flex: 1,
      renderCell: (params) => {
        const truncatedText =
          params.value && params.value.length > 15
            ? `${params.value.substring(0, 15)}...`
            : params.value;
        return <span>{truncatedText}</span>;
      },
    },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      flex: 1,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? "Invalid Date" : date.toISOString().split("T")[0];
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      getActions: (params) => [
        <GridActionsCellItem
          key={params.id}
          icon={<EditIcon style={{ color: "white" }} />}
          label="Edit"
          onClick={() => handleEditClick(params.row)}
        />,
      ],
    },
  ];

  const theme = createTheme({
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            color: "white",
            border: "1px solid #e9a132",
            backgroundColor: "transparent",
            "& .MuiDataGrid-columnHeaders": {
              color: "white",
              backgroundColor: "#1c1c1c",
              borderBottom: "2px solid #e9a132",
            },
            "& .MuiDataGrid-cell": {
              color: "white",
              borderBottom: "1px solid #e9a132",
            },
            "& .MuiDataGrid-iconSeparator": {
              color: "white",
            },
            "& .MuiDataGrid-sortIcon": {
              color: "white",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "white",
            },
            "& .MuiSvgIcon-root": {
              color: "white",
            },
            "& .MuiDataGrid-footerContainer": {
              color: "white",
              backgroundColor: "#1c1c1c",
              borderTop: "2px solid #e9a132",
            },
            "& .MuiTablePagination-root": {
              color: "white",
            },
            "& .MuiTablePagination-actions": {
              color: "white",
            },
            "& .MuiInputBase-root": {
              color: "white",
            },
            "& .MuiSelect-icon": {
              color: "white",
            },
            "& .MuiDataGrid-virtualScroller": {
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#e9a132",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#d0892f",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#333",
                borderRadius: "8px",
              },
            },
          },
        },
      },
    },
  });

  const colorfulTextStyle = {
    background: "linear-gradient(45deg, #ffa500, #ffffff, #ffa500)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: "bold",
    marginBottom: "20px",
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{ height: "70vh", width: "98%" }}>
        <Typography variant="h4" sx={colorfulTextStyle}>
          Client Data:
        </Typography>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress sx={{ color: "#e9a132" }} />
          </Box>
        ) : (
          <DataGrid
            rows={clients}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            getRowId={(row) => row.id}
            key={JSON.stringify(clients)}
            sx={{
              color: "white",
              border: "1px solid #e9a132",
              backgroundColor: "transparent",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-displayedRows":
                {
                  color: "white",
                },
              "& .MuiInputBase-input": {
                color: "white",
              },
            }}
          />
        )}
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: "#1c1c1c",
              color: "white",
            },
          }}
        >
          <DialogTitle>Edit Client</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="clientName"
              label="Client Name"
              type="text"
              fullWidth
              variant="outlined"
              value={editClient?.clientName || ""}
              onChange={(e) =>
                setEditClient({ ...editClient, clientName: e.target.value })
              }
              InputLabelProps={{
                style: {
                  color: "white",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "white",
                  },
                  "&:hover fieldset": {
                    borderColor: "#e9a132",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#e9a132",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "white",
                },
              }}
            />
            <TextField
              margin="dense"
              id="dateOfBirth"
              label="Date of Birth"
              type="date"
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
                style: { color: "white" },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "white",
                  },
                  "&:hover fieldset": {
                    borderColor: "#e9a132",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#e9a132",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "white",
                },
              }}
              value={editClient?.dateOfBirth || ""}
              onChange={(e) => {
                const value = e.target.value;
                const today = new Date().toISOString().split("T")[0];
                if (value <= today) {
                  setEditClient({ ...editClient, dateOfBirth: value });
                }
              }}
              inputProps={{
                max: new Date().toISOString().split("T")[0], // Set the max attribute to today's date
              }}
            />
            <TextField
              margin="dense"
              id="profileDetails"
              label="Profile Details"
              type="text"
              fullWidth
              variant="outlined"
              inputProps={{ maxLength: 500 }}
              value={editClient?.profileDetails || ""}
              onChange={(e) =>
                setEditClient({ ...editClient, profileDetails: e.target.value })
              }
              InputLabelProps={{
                style: {
                  color: "white",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "white",
                  },
                  "&:hover fieldset": {
                    borderColor: "#e9a132",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#e9a132",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "white",
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "#444",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              sx={{
                backgroundColor: "#e9a132",
                color: "white",
                "&:hover": {
                  backgroundColor: "#d0892f",
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmOpen}
          onClose={handleConfirmClose}
          PaperProps={{
            sx: {
              backgroundColor: "#1c1c1c",
              color: "white",
            },
          }}
        >
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            Are you sure you want to save the changes?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleConfirmClose}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "#444",
                },
              }}
            >
              No
            </Button>
            <Button
              onClick={handleConfirmSave}
              sx={{
                backgroundColor: "#e9a132",
                color: "white",
                "&:hover": {
                  backgroundColor: "#d0892f",
                },
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
});

GetClient.displayName = "GetClient";

export default GetClient;
